import React, { useState } from 'react';
import { Wrench, Settings, Search, Disc, Wind, Target, Car, ClipboardCheck, Circle } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { DialogTrigger } from './ui/dialog';
import { useLanguage } from '../contexts/LanguageContext';
import { 
  services, 
  gearboxTypes, 
  repairMethods,
  maintenanceTypes, 
  diagnosticDetails, 
  brakingDetails, 
  airConditioningDetails, 
  geometryDetails, 
  bodyworkDetails, 
  inspectionDetails, 
  rimsDetails 
} from '../mock';
import { GearboxModal, MaintenanceModal, GenericServiceModal } from './ServiceModal';

const iconMap = {
  wrench: Wrench,
  settings: Settings,
  search: Search,
  disc: Disc,
  wind: Wind,
  target: Target,
  car: Car,
  'clipboard-check': ClipboardCheck,
  'circle': Circle
};

const serviceModalTitles = {
  fr: {
    3: 'Diagnostic Électronique',
    4: 'Systèmes de Freinage',
    5: 'Service Climatisation',
    6: 'Géométrie & Parallélisme',
    7: 'Carrosserie & Peinture',
    8: 'Pré-Contrôle Technique',
    9: 'Jantes & Roues'
  },
  en: {
    3: 'Electronic Diagnostics',
    4: 'Braking Systems',
    5: 'Air Conditioning Service',
    6: 'Geometry & Alignment',
    7: 'Bodywork & Paint',
    8: 'Pre-MOT Inspection',
    9: 'Wheels & Rims'
  }
};

const serviceModalSubtitles = {
  fr: {
    3: 'Diagnostic complet avec équipement professionnel pour identifier tous les problèmes de votre véhicule',
    4: 'Services complets de freinage pour garantir votre sécurité et des performances optimales',
    5: 'Services d\'entretien et réparation de climatisation pour votre confort toute l\'année',
    6: 'Réglages précis pour une meilleure tenue de route et une usure réduite des pneus',
    7: 'Réparation et rénovation complète de carrosserie pour restaurer l\'apparence de votre véhicule',
    8: 'Inspection complète avant contrôle technique pour identifier et résoudre les problèmes',
    9: 'Réparation, remplacement et répliques de jantes pour tous types de véhicules'
  },
  en: {
    3: 'Complete diagnostics with professional equipment to identify all vehicle problems',
    4: 'Complete braking services to ensure your safety and optimal performance',
    5: 'Air conditioning maintenance and repair services for year-round comfort',
    6: 'Precise adjustments for better handling and reduced tire wear',
    7: 'Complete bodywork repair and renovation to restore your vehicle\'s appearance',
    8: 'Complete pre-technical inspection to identify and resolve issues',
    9: 'Rim repair, replacement and replicas for all vehicle types'
  }
};

export const Services = () => {
  const { language, t } = useLanguage();
  const isEnglish = language === 'en';
  const [openModalId, setOpenModalId] = useState(null);

  const getServiceDetails = (serviceId) => {
    const detailsMap = {
      1: { component: 'gearbox' },
      2: { component: 'maintenance' },
      3: { data: diagnosticDetails },
      4: { data: brakingDetails },
      5: { data: airConditioningDetails },
      6: { data: geometryDetails },
      7: { data: bodyworkDetails },
      8: { data: inspectionDetails },
      9: { data: rimsDetails }
    };
    return detailsMap[serviceId];
  };

  const renderModal = (serviceId) => {
    const details = getServiceDetails(serviceId);
    
    if (serviceId === 1) {
      return (
        <GearboxModal 
          isOpen={openModalId === serviceId}
          onOpenChange={(open) => setOpenModalId(open ? serviceId : null)}
          gearboxTypes={gearboxTypes}
          repairMethods={repairMethods}
          isEnglish={isEnglish}
        />
      );
    }
    
    if (serviceId === 2) {
      return (
        <MaintenanceModal 
          isOpen={openModalId === serviceId}
          onOpenChange={(open) => setOpenModalId(open ? serviceId : null)}
          maintenanceTypes={maintenanceTypes}
          isEnglish={isEnglish}
        />
      );
    }
    
    return (
      <GenericServiceModal 
        isOpen={openModalId === serviceId}
        onOpenChange={(open) => setOpenModalId(open ? serviceId : null)}
        title={serviceModalTitles[language][serviceId]}
        subtitle={serviceModalSubtitles[language][serviceId]}
        details={details.data}
        isEnglish={isEnglish}
      />
    );
  };

  return (
    <section id="services" className="py-20 bg-slate-50">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-800 mb-4">
            {t.services.title}
          </h2>
          <p className="text-lg text-slate-600">
            {t.services.subtitle}
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {services.map((service, index) => {
            const IconComponent = iconMap[service.icon] || Wrench;
            const translatedService = t.services.items[index];
            
            return (
              <div key={service.id}>
                <Card 
                  className="group hover:shadow-xl transition-all duration-300 border-slate-200 hover:border-red-400 cursor-pointer overflow-hidden h-full"
                  onClick={() => setOpenModalId(service.id)}
                >
                  {/* Service Image */}
                  <div className="relative h-48 overflow-hidden">
                    <img 
                      src={service.image}
                      alt={translatedService.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 to-transparent"></div>
                    
                    {/* Icon Badge */}
                    <div className="absolute bottom-4 left-4 w-14 h-14 bg-red-600 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform duration-300 shadow-lg">
                      <IconComponent className="text-white" size={28} />
                    </div>
                  </div>

                  <CardHeader>
                    <CardTitle className="text-xl font-bold text-slate-800 group-hover:text-red-600 transition-colors">
                      {translatedService.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-slate-600 leading-relaxed">
                      {translatedService.description}
                    </CardDescription>
                    <p className="text-red-600 font-semibold mt-4 text-sm">
                      {isEnglish ? '→ Click to learn more' : '→ Cliquez pour en savoir plus'}
                    </p>
                  </CardContent>
                </Card>
                
                {/* Render the appropriate modal */}
                {renderModal(service.id)}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
